'use client'

import { useCallback, useMemo, useState } from 'react'
import type {
  UploadFileState,
  UploadPhase,
  UploadProgressCallbacks,
  UploadProgressSnapshot,
} from './upload-progress'

/** The confirm dialog the cancel flow goes through — shaped like `useKiaoConfirm`. */
export type UploadConfirmFn = (options: {
  title: string
  message?: string
  confirmText: string
  cancelText: string
}) => Promise<boolean>

/** Copy for the cancel confirmation, resolved by the caller so this stays i18n-free. */
export interface UploadCancelCopy {
  title: string
  message: string
  confirmText: string
  cancelText: string
  /**
   * Deep red on the confirm button: agreeing here throws away every byte
   * uploaded so far and the transfer restarts from zero. Reaching for the
   * footer's Cancel is not the destructive act — agreeing to this is.
   */
  tone: 'danger'
}

/**
 * `cancelled` is a resting state, not a transient one: the dialog goes back to
 * being editable and says so, with the file queue untouched.
 */
export type UploadSubmitPhase = 'idle' | UploadPhase | 'cancelled'

/** What the queue shows per file. `waiting` is a file whose turn hasn't come. */
export type UploadFileDisplayState = UploadFileState | 'waiting' | 'checking' | 'checked'

export interface UploadFileProgress {
  state: UploadFileDisplayState
  /** 0..1, or null when this file has no measurable progress (yet). */
  fraction: number | null
}

export interface UploadSubmitProgress {
  phase: UploadSubmitPhase
  /** 1-based position of the file being checked during `preparing`, else null. */
  probeIndex: number | null
  fileCount: number
  /** 0..1 across all bytes, or null when there is nothing trustworthy to show. */
  overall: number | null
  files: UploadFileProgress[]
  doneCount: number
}

const IDLE: UploadSubmitProgress = {
  phase: 'idle',
  probeIndex: null,
  fileCount: 0,
  overall: null,
  files: [],
  doneCount: 0,
}

/**
 * The upload dialog's own state machine, driven by the submit call's callbacks.
 *
 * It lives outside the dialog component for two reasons: the dialog is already
 * one of the largest files in the app and may only get smaller, and a state
 * machine with five states and three event sources is worth testing without a
 * DOM.
 */
export function useUploadSubmitProgress() {
  const [progress, setProgress] = useState<UploadSubmitProgress>(IDLE)
  const reset = useCallback(() => {
    setProgress(IDLE)
  }, [])

  const beginPreparing = useCallback((fileCount: number) => {
    setProgress({
      phase: 'preparing',
      probeIndex: fileCount > 0 ? 1 : null,
      fileCount,
      overall: null,
      // Every file gets a row immediately, so the list does not reflow a second
      // later when the first one starts moving.
      files: Array.from({ length: fileCount }, () => ({ state: 'waiting', fraction: null })),
      doneCount: 0,
    })
  }, [])

  /** Called as each queued file is checked before upload. `index` is 0-based. */
  const noteFileChecked = useCallback((index: number, total: number) => {
    setProgress((prev) => {
      if (prev.phase !== 'preparing') return prev
      const files = prev.files.map((file, i) =>
        i < index ? { ...file, state: 'checked' as const } : i === index ? { ...file, state: 'checking' as const } : file
      )
      return { ...prev, files, probeIndex: Math.min(index + 1, total) }
    })
  }, [])

  const markCancelled = useCallback(() => {
    setProgress((prev) => ({ ...IDLE, phase: 'cancelled', fileCount: prev.fileCount }))
  }, [])

  /**
   * The only way out of an upload in flight. Behind a confirmation on purpose,
   * and the abort happens ONLY after the user says yes — a mis-click that opened
   * the dialog must leave the transfer untouched. Answers whether it cancelled,
   * so the caller can keep its own submitting flag in step.
   */
  const requestCancel = useCallback(
    async (confirm: UploadConfirmFn, copy: UploadCancelCopy, abort: () => void) => {
      const confirmed = await confirm(copy)
      if (!confirmed) return false
      abort()
      setProgress((prev) => ({ ...IDLE, phase: 'cancelled', fileCount: prev.fileCount }))
      return true
    },
    []
  )

  const submitCallbacks = useMemo(() => {
    return {
      onPhase: (phase: UploadPhase) => {
        setProgress((prev) => {
          // A cancelled or reset run must not be re-animated by a callback still
          // draining from the aborted request.
          if (prev.phase === 'idle' || prev.phase === 'cancelled') return prev
          return {
            ...prev,
            phase,
            probeIndex: phase === 'preparing' ? prev.probeIndex : null,
            // Finalizing has no percentage — the copy takes over. Keeping the
            // last number frozen on screen would read as a stall.
            overall: phase === 'finalizing' ? null : prev.overall,
            files:
              phase === 'uploading'
                ? prev.files.map((file) =>
                    file.state === 'checked' || file.state === 'checking'
                      ? { ...file, state: 'waiting' as const }
                      : file
                  )
                : prev.files,
          }
        })
      },
      onUploadProgress: (snapshot: UploadProgressSnapshot) => {
        setProgress((prev) => {
          if (prev.phase !== 'uploading') return prev
          return {
            ...prev,
            overall: snapshot.overall,
            files: prev.files.map((file, index) => ({
              ...file,
              fraction: snapshot.files[index] ?? file.fraction,
            })),
          }
        })
      },
      onFileState: (index: number, state: UploadFileState) => {
        setProgress((prev) => {
          if (prev.phase === 'idle' || prev.phase === 'cancelled') return prev
          const files = prev.files.map((file, i) =>
            i === index
              ? { ...file, state, fraction: state === 'done' ? 1 : file.fraction }
              : file
          )
          return { ...prev, files, doneCount: files.filter((f) => f.state === 'done').length }
        })
      },
    } satisfies Required<UploadProgressCallbacks>
  }, [])

  return {
    progress,
    beginPreparing,
    noteFileChecked,
    markCancelled,
    requestCancel,
    reset,
    submitCallbacks,
  }
}
